import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import axiosInstance from "../../../utils/axiosInstance";
import { useNavigate, useLocation } from "react-router-dom";
import { completeLogin } from "../../../utils/auth";
import { MFA_NUDGE_DISMISS_KEY } from "../../../components/Layout/MfaNudgeBanner";
import { Mail, Lock, AlertCircle, Eye, EyeOff } from "lucide-react";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const logout = params.get("logout");

  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (logout === "true") {
      localStorage.removeItem("authToken");
      document.cookie =
        "SessionToken=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
      // A dismissed MFA nudge shouldn't stay dismissed forever - only for
      // the login session the user dismissed it in. sessionStorage alone
      // doesn't clear on logout (only when the tab actually closes), so
      // clear it explicitly here, the one place every logout path
      // (manual logout, 401 session-expiry) already funnels through.
      sessionStorage.removeItem(MFA_NUDGE_DISMISS_KEY);
    } else {
      const authToken = localStorage.getItem("authToken");
      if (authToken && authToken.length) {
        navigate("/");
      }
    }
  }, [logout, navigate]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setServerError(null);

    try {
      const response = await axiosInstance.post("/api/login", data);

      if (response.data?.mfaRequired) {
        sessionStorage.setItem("mfaChallengeToken", response.data.challengeToken);
        navigate("/auth/mfa-challenge");
      } else if (response.data?.accessToken) {
        completeLogin(response.data.accessToken, navigate);
      } else {
        setServerError("Invalid response from server. Please try again.");
      }
    } catch (error: any) {
      console.error("Login error:", error);

      // Handle different error scenarios
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.detail || error.response.data?.message || error.response.data?.error;

        // Use backend message if available, otherwise use status-specific messages
        if (message) {
          setServerError(message);
        } else if (status === 401) {
          setServerError("Invalid email or password. Please try again.");
        } else if (status === 403) {
          setServerError("Access forbidden. Please contact support.");
        } else if (status === 404) {
          setServerError("User not found. Please check your email.");
        } else if (status === 429) {
          setServerError("Too many login attempts. Please try again later.");
        } else if (status >= 500) {
          setServerError("Server error. Please try again later.");
        } else {
          setServerError("Login failed. Please try again.");
        }
      } else if (error.request) {
        setServerError("Network error. Please check your connection and try again.");
      } else {
        setServerError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Server Error Message */}
      {serverError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 animate-shake">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">{serverError}</p>
          </div>
          <button
            type="button"
            onClick={() => setServerError(null)}
            className="text-red-600 hover:text-red-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email address
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className={`w-5 h-5 ${errors.email ? 'text-red-400' : 'text-gray-400'}`} />
          </div>
          <input
            {...register("email")}
            type="email"
            id="email"
            autoComplete="email"
            className={`
              block w-full pl-10 pr-3 py-3 border rounded-lg
              focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all
              ${
                errors.email
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              }
              disabled:bg-gray-50 disabled:cursor-not-allowed
            `}
            placeholder="you@example.com"
            disabled={isLoading}
          />
        </div>
        {errors.email && (
          <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
          Password
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className={`w-5 h-5 ${errors.password ? 'text-red-400' : 'text-gray-400'}`} />
          </div>
          <input
            {...register("password")}
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            className={`
              block w-full pl-10 pr-12 py-3 border rounded-lg
              focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all
              ${
                errors.password
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              }
              disabled:bg-gray-50 disabled:cursor-not-allowed
            `}
            placeholder="Enter your password"
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
        {errors.password && (
          <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Remember Me & Forgot Password (resets are done by Adrsta, no self-service) */}
      <div className="flex items-center justify-between">
        <label className="flex items-center">
          <input
            type="checkbox"
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-all"
          />
          <span className="ml-2 text-sm text-gray-600">Remember me</span>
        </label>
        <span className="text-sm text-gray-600">
          Forgot password? <span className="font-medium text-blue-600">Contact Adrsta</span>
        </span>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className={`
          w-full flex items-center justify-center gap-2 py-3 px-4 border border-transparent rounded-lg
          text-white font-medium text-sm
          transition-all duration-200
          ${
            isLoading
              ? 'bg-blue-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] shadow-lg hover:shadow-xl'
          }
        `}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Signing in...
          </>
        ) : (
          <>
            Sign in
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </>
        )}
      </button>
    </form>
  );
};

export default LoginForm;
